import {
  useCallback, useState, useEffect, useRef, Children,
} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Icon from '@/core/icon';

function CarouselEmbla({
  children, slidesToScroll, loop, align, fromCluster, fromHome, onIndexChange, fromSpacePublic, startIndex, enableKeyboardNavigation,
}) {
  const [activeDot, setActiveDot] = useState(startIndex || 0);
  const [totalSlides, setTotalSlides] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    ...(align ? { align } : {}),
    ...(loop ? { loop } : {}),
    ...(slidesToScroll ? { slidesToScroll } : {}),
    ...(startIndex ? { startIndex } : {}),

  });

  /*
    `onIndexChange` arrive en général sous forme de fonction inline (recréée à chaque rendu du
    parent). On la garde dans une ref pour que l'abonnement à l'évènement `select` ne dépende
    QUE de `emblaApi` : sans ça, chaque rendu du parent ré-exécutait l'effet.
  */
  const onIndexChangeRef = useRef(onIndexChange);
  onIndexChangeRef.current = onIndexChange;

  useEffect(() => {
    if (!emblaApi) return undefined;
    const onSelect = () => {
      const currentIndex = emblaApi.selectedScrollSnap();
      setActiveDot(currentIndex);
      if (onIndexChangeRef.current) {
        onIndexChangeRef.current(currentIndex);
      }
    };
    /*
      `reInit` est aussi émis par embla lui-même au redimensionnement : le nombre de points de
      pagination dépend de la largeur pour les carrousels à slides partielles (home, villes).
    */
    const onReInit = () => {
      const scrollSnapList = emblaApi.scrollSnapList();
      setTotalSlides(scrollSnapList ? scrollSnapList.length : 0);
      onSelect();
    };
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onReInit);
    // `off` indispensable : sans lui les handlers s'empilaient à chaque ré-abonnement.
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onReInit);
    };
  }, [emblaApi]);

  /*
    reInit UNIQUEMENT quand le nombre de slides change.
    Avant, l'effet dépendait de `children` — un nouveau tableau à CHAQUE rendu du parent — donc
    le moindre re-rendu (hover sur la carte, remontée de l'index via `onIndexChange`) rejouait
    `emblaApi.reInit()`. Or embla émet `select` au DÉBUT du défilement (ScrollTo), pendant que
    l'animation tourne : le parent se re-rendait, l'effet repartait, et `reInit()` détruit le
    moteur puis le recrée avec `startIndex = selectedScrollSnap()`, c'est-à-dire déjà l'image
    CIBLE. Résultat : l'image changeait d'un coup, sans glissement.
  */
  const slideCount = Children.count(children);
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit();
    const scrollSnapList = emblaApi.scrollSnapList();
    setTotalSlides(scrollSnapList ? scrollSnapList.length : 0);
    setActiveDot(emblaApi.selectedScrollSnap());
  }, [emblaApi, slideCount, startIndex]);

  const scrollPrev = useCallback(
    (e) => {
      e.stopPropagation();
      if (emblaApi) {
        emblaApi.scrollPrev();
      }
    },
    [emblaApi],
  );

  const scrollNext = useCallback(
    (e) => {
      e.stopPropagation();
      if (emblaApi) {
        emblaApi.scrollNext();
      }
    },
    [emblaApi],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (enableKeyboardNavigation && e.keyCode === 37) {
        scrollPrev(e);
      } else if (enableKeyboardNavigation && e.keyCode === 39) {
        scrollNext(e);
      }
    },
    [enableKeyboardNavigation, scrollPrev, scrollNext],
  );

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (enableKeyboardNavigation) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [enableKeyboardNavigation, handleKeyDown]);

  return (
    <div className="relative h-full overflow-hidden transition group">
      { (!fromCluster && !fromHome && !fromSpacePublic) && (
      <>
        {' '}
        <div className="absolute z-10 flex items-center h-full ml-4 transition arrow">
          <button aria-label="prev" className="embla__prev" onClick={scrollPrev}>
            <div className="flex items-center justify-center transition bg-white rounded-full size-8 shadow-button group hover:scale-110">
              <Icon
                className="text-purple-5/80 group-hover:text-purple-5 transform rotate-180 size-4.5"
                name="next-triangle"
              />
            </div>
          </button>
        </div>
        <div className="absolute right-0 z-10 flex justify-end h-full mr-4 transition arrow">
          <button aria-label="next" className="embla__next" onClick={scrollNext}>
            <div className="flex items-center justify-center transition bg-white rounded-full size-8 shadow-button group hover:scale-110">
              <Icon
                className="text-purple-5/80 group-hover:text-purple-5 size-4.5"
                name="next-triangle"
              />
            </div>
          </button>
        </div>
      </>
      ) }
      {fromHome && (
      <>
        {' '}
        <div className="absolute z-10 flex items-center h-full ml-6 transition arrow">
          <button aria-label="prev" className="embla__prev" onClick={scrollPrev}>
            <div className="flex items-center justify-center w-10 h-10 transition rounded-full bg-purple-6 shadow-button hover:scale-110">
              <Icon
                className="transform rotate-180 size-6 text-purple-0"
                name="next-triangle"
              />
            </div>
          </button>
        </div>
        <div className="absolute right-0 z-10 flex justify-end h-full mr-4 transition arrow">
          <button aria-label="next" className="embla__next" onClick={scrollNext}>
            <div className="flex items-center justify-center w-10 h-10 transition rounded-full bg-purple-6 shadow-button hover:scale-110">
              <Icon
                className="size-6 text-purple-0"
                name="next-triangle"
              />
            </div>
          </button>
        </div>
      </>
      )}
      {fromSpacePublic && (
      <>
        {' '}
        <div className="absolute z-10 flex items-center h-full ml-8 transition arrow">
          <button aria-label="prev" className="embla__prev" onClick={scrollPrev}>
            <div className="flex items-center justify-center w-12 h-12 transition bg-black border rounded-full shadow-button border-dark hover:bg-black-light hover:scale-110">
              <Icon
                className="w-5 h-5 mr-1 text-white transform rotate-180"
                name="next-triangle"
              />
            </div>
          </button>
        </div>
        <div className="absolute right-0 z-10 flex justify-end h-full mr-8 transition arrow">
          <button aria-label="next" className="embla__next" onClick={scrollNext}>
            <div className="flex items-center justify-center w-12 h-12 transition bg-black border rounded-full shadow-button border-dark hover:bg-black-light hover:scale-110">
              <Icon
                className="w-5 h-5 ml-1 text-white"
                name="next-triangle"
              />
            </div>
          </button>
        </div>
      </>
      )}
      <div className="embla">
        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container">{children}</div>
        </div>
      </div>
      {(!fromCluster && !fromHome && !fromSpacePublic) && (
      <div className="absolute bottom-0 w-full flex justify-center mb-3 gap-x-[5px]">
        {Array.from({ length: totalSlides }, (_, index) => (
          <div
            key={index}
            className={`w-[6px] h-[6px] rounded-full bg-white cursor-pointer ${
              index === activeDot ? 'scale-125' : 'bg-white/60'
            }`}
            onClick={(e) => { e.stopPropagation(); emblaApi?.scrollTo(index); }}
          />
        ))}
      </div>
      )}
      {fromSpacePublic && (
      <div className="absolute flex justify-center w-full mb-6 text-sm top-8 text-purple-5/50 gap-x-2">
        <div className="">
          {activeDot + 1}
          {' '}
          /
          {' '}
          {totalSlides}
        </div>
      </div>
      )}
      {fromCluster
       && (
       <div className="w-full border-t border-purple-1 lg:mt-2">
         <div className="flex">
           <button
             className="flex items-center justify-center w-1/2 transition border-r rounded-none group hover:bg-purple/10 border-purple-1"
             onClick={scrollPrev}
           >
             <Icon
               className="h-5 mr-1 text-black rotate-180 w-7 group-hover:text-purple"
               name="next"
             />
           </button>
           <button
             className="flex items-center justify-center w-1/2 h-10 transition rounded-none group hover:bg-purple/10"
             onClick={scrollNext}
           >
             <Icon
               className="h-5 ml-1 text-black w-7 group-hover:text-purple"
               name="next"
             />
           </button>
         </div>
       </div>
       )}
    </div>
  );
}

export default CarouselEmbla;
